import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LegalSection } from "@/components/legal/LegalSection";
import { TableOfContents } from "@/components/legal/TableOfContents";
import { CookiePreferences } from "@/components/legal/CookiePreferences";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { FileText, Shield, Cookie, Settings, CheckCircle2, Briefcase, ShieldCheck, Ban, RefreshCw, Scale, Menu, ArrowUp, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
const tocItems = [{
  id: "terms-of-service",
  title: "Terms of Service",
  icon: <FileText className="w-5 h-5" />
}, {
  id: "privacy-policy",
  title: "Privacy Policy",
  icon: <Shield className="w-5 h-5" />
}, {
  id: "cookie-policy",
  title: "Cookie Policy",
  icon: <Cookie className="w-5 h-5" />
}, {
  id: "cookie-preferences",
  title: "Cookie Preferences",
  icon: <Settings className="w-5 h-5" />
}, {
  id: "compliance",
  title: "Compliance Information",
  icon: <CheckCircle2 className="w-5 h-5" />
}, {
  id: "seller-agreement",
  title: "Seller Agreement",
  icon: <Briefcase className="w-5 h-5" />
}, {
  id: "buyer-protection",
  title: "Buyer Protection",
  icon: <ShieldCheck className="w-5 h-5" />
}, {
  id: "prohibited-items",
  title: "Prohibited Items",
  icon: <Ban className="w-5 h-5" />
}, {
  id: "refund-policy",
  title: "Refund Policy",
  icon: <RefreshCw className="w-5 h-5" />
}, {
  id: "dispute-resolution",
  title: "Dispute Resolution",
  icon: <Scale className="w-5 h-5" />
}, {
  id: "contact-support",
  title: "Contact Support",
  icon: <Mail className="w-5 h-5" />
}];
const Terms = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const handleScroll = () => {
    setShowScrollTop(window.scrollY > 400);
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle initial scroll to section from anchor link
  useEffect(() => {
    const timer = setTimeout(() => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  return <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 lg:py-12">
        {/* Header */}
        <div className="mb-8 lg:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Legal & Policy Center</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Welcome to Circle Pro Hub's comprehensive legal documentation. Here you'll find all our terms, policies, and agreements in one place.
          </p>
        </div>

        <div className="flex gap-8 lg:gap-12 relative">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-80 shrink-0">
            <div className="sticky top-24 space-y-4">
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">
                  Table of Contents
                </h3>
                <TableOfContents items={tocItems} />
              </div>
            </div>
          </aside>

          {/* Mobile Menu */}
          <div className="lg:hidden fixed bottom-6 right-6 z-50">
            <Sheet>
              <SheetTrigger asChild>
                <Button size="lg" className="rounded-full shadow-lg h-14 w-14 p-0">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                <h3 className="font-semibold mb-6 text-lg">Table of Contents</h3>
                <TableOfContents items={tocItems} isMobile />
              </SheetContent>
            </Sheet>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Terms of Service */}
            <LegalSection id="terms-of-service" title="Terms of Service" icon={<FileText className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Acceptance of Terms</h3>
              <p className="mb-4">
                By accessing and using Circle Pro Hub ("the Platform"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Age Restriction</h3>
              <p className="mb-4">
                You must be 18 years or older to use this platform. By accessing or using our services, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into this agreement.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Marketplace Disclaimer</h3>
              <p className="mb-4">
                Circle Pro Hub serves as a venue for buyers and sellers to transact. All products and services are provided by independent vendors. Pricing information and copay examples are illustrative and subject to change. Vendors are solely responsible for fulfillment, warranties, and customer service. Verified vendors have undergone identity and business verification, but we do not guarantee the quality or legality of products/services offered.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Geographic Restrictions</h3>
              <p className="mb-4">
                Services are available only where permitted by law. Some products may not be available in all jurisdictions. Users are responsible for ensuring their use of the Platform complies with local, state, and federal laws in their jurisdiction.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Account Responsibilities</h3>
              <p className="mb-4">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized access or security breach.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. User-Generated Content</h3>
              <p className="mb-4">
                Users are responsible for all content they post on the Platform, including listings, reviews, comments, and messages. You grant Circle Pro Hub a non-exclusive, worldwide license to use, display, and distribute your content. We reserve the right to remove content that violates our policies, infringes on intellectual property rights, or is deemed harmful or inappropriate.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Tax Disclaimer</h3>
              <p className="mb-4">
                Buyers and sellers are responsible for determining and remitting applicable taxes on transactions. Circle Pro Hub does not collect, remit, or provide tax advice. You should consult with a qualified tax professional regarding your tax obligations related to transactions on the Platform.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Acceptable Use</h3>
              <p className="mb-4">
                You agree not to use the Platform for any unlawful purpose or in any way that could damage, disable, overburden, or impair our services. Prohibited activities include but are not limited to fraud, harassment, spam, and unauthorized data collection.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">9. Intellectual Property</h3>
              <p className="mb-4">
                All content on the Platform, including text, graphics, logos, and software, is the property of Circle Pro Hub or its licensors and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">10. Indemnification Notice</h3>
              <p className="mb-4">
                Users agree to indemnify, defend, and hold harmless Circle Pro Hub, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney's fees) arising from: (a) your use of the Platform, (b) your violation of these Terms, (c) your violation of any rights of another party, or (d) any content you post or transmit through the Platform.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">11. "As-Is" Disclaimer</h3>
              <p className="mb-4">
                The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Platform will be uninterrupted, error-free, or secure.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">12. Limitation of Liability</h3>
              <p className="mb-4">
                To the maximum extent permitted by law, Circle Pro Hub shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform, including but not limited to loss of profits, data, or goodwill. Our total liability shall not exceed the amount paid by you in the past 12 months, or $100, whichever is greater.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">13. Modifications</h3>
              <p>
                We reserve the right to modify these terms at any time. We will provide notice of material changes via email or Platform notification. Continued use of the Platform after changes constitutes acceptance of the modified terms.
              </p>
            </LegalSection>

            {/* Privacy Policy */}
            <LegalSection id="privacy-policy" title="Privacy Policy" icon={<Shield className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Information We Collect</h3>
              <p className="mb-4">
                We collect information you provide directly to us, including name, email address, payment information, and any other information you choose to provide. We also automatically collect certain information about your device and usage of our services.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. How We Use Your Information</h3>
              <p className="mb-4">
                We use your information to provide, maintain, and improve our services; process transactions; send communications; personalize your experience; and protect against fraud and abuse.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Information Sharing</h3>
              <p className="mb-4">
                We do not sell your personal information. We may share information with service providers, business partners, and when required by law. All third parties are contractually obligated to protect your data.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Data Security</h3>
              <p className="mb-4">
                We implement industry-standard security measures to protect your information, including encryption, secure servers, and regular security audits. However, no method of transmission over the internet is 100% secure.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Your Rights (GDPR & CCPA)</h3>
              <p className="mb-4">
                You have the right to access, correct, delete, and export your personal data. You may also object to certain processing activities and withdraw consent at any time. Contact us to exercise these rights.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Data Retention</h3>
              <p>
                We retain your information for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your account and data at any time.
              </p>
            </LegalSection>

            {/* Cookie Policy */}
            <LegalSection id="cookie-policy" title="Cookie Policy" icon={<Cookie className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. What Are Cookies</h3>
              <p className="mb-4">
                Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience by remembering your preferences and analyzing site usage.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Types of Cookies We Use</h3>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li><strong>Necessary Cookies:</strong> Essential for the website to function properly</li>
                <li><strong>Functional Cookies:</strong> Enable personalized features and preferences</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Third-Party Cookies</h3>
              <p className="mb-4">
                We use third-party services like Google Analytics and payment processors that may set their own cookies. These are governed by the respective third party's privacy policy.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Managing Cookies</h3>
              <p>
                You can control and delete cookies through your browser settings or by using our Cookie Preferences tool below. Note that disabling certain cookies may affect site functionality.
              </p>
            </LegalSection>

            {/* Cookie Preferences */}
            <LegalSection id="cookie-preferences" title="Cookie Preferences" icon={<Settings className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <p className="mb-6">
                Manage your cookie preferences below. Your choices will be saved and applied across the site.
              </p>
              <CookiePreferences />
            </LegalSection>

            {/* Compliance Information */}
            <LegalSection id="compliance" title="Compliance Information" icon={<CheckCircle2 className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Regulatory Compliance</h3>
              <p className="mb-4">
                Circle Pro Hub complies with all applicable federal, state, and international regulations, including GDPR (General Data Protection Regulation), CCPA (California Consumer Privacy Act), and CAN-SPAM Act.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Industry Standards</h3>
              <p className="mb-4">
                We adhere to industry best practices for data security, including PCI DSS for payment processing, SOC 2 Type II compliance, and ISO 27001 information security standards.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Accessibility</h3>
              <p className="mb-4">
                We are committed to making our platform accessible to all users and comply with WCAG 2.1 Level AA standards.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Certifications</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>SSL/TLS Encryption Certified</li>
                <li>PCI DSS Level 1 Compliant</li>
                <li>SOC 2 Type II Certified</li>
                <li>GDPR Ready</li>
              </ul>
            </LegalSection>

            {/* Seller Agreement */}
            <LegalSection id="seller-agreement" title="Seller Agreement" icon={<Briefcase className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Eligibility</h3>
              <p className="mb-4">
                To sell on Circle Pro Hub, you must be a legitimate business entity or authorized representative offering real estate agent tools and services. All sellers undergo a verification process.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Listing Requirements</h3>
              <p className="mb-4">
                Sellers must provide accurate, complete, and up-to-date information about their products/services, including pricing, features, terms of service, and any limitations or restrictions.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Commission Structure</h3>
              <p className="mb-4">
                Circle Pro Hub charges a commission on successful transactions. Standard commission is 15% for services and 10% for courses. Select partners may receive reduced rates based on volume and partnership level.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Payment Terms</h3>
              <p className="mb-4">
                Payments are processed monthly, within 15 days after the end of each month. Sellers must maintain valid payment information and meet minimum payout thresholds.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Seller Obligations</h3>
              <p className="mb-4">
                Sellers are responsible for fulfilling orders, providing customer support, honoring refund policies, and maintaining professional conduct. Failure to meet obligations may result in suspension or termination.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Prohibited Conduct</h3>
              <p>
                Sellers may not engage in fraudulent activities, misrepresent products, manipulate reviews, contact customers outside the platform without permission, or violate intellectual property rights.
              </p>
            </LegalSection>

            {/* Buyer Protection */}
            <LegalSection id="buyer-protection" title="Buyer Protection" icon={<ShieldCheck className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Purchase Guarantees</h3>
              <p className="mb-4">
                All purchases on Circle Pro Hub are protected by our Buyer Protection Program. If you don't receive your product/service or it's significantly different from the description, you may be eligible for a refund.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Refund Eligibility</h3>
              <p className="mb-4">
                You may request a refund within 30 days of purchase if: (1) the service was not delivered, (2) the service doesn't match the description, (3) the seller violated our policies, or (4) there's a technical issue preventing access.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Filing a Claim</h3>
              <p className="mb-4">
                To file a buyer protection claim, contact our support team with your order details, description of the issue, and any supporting documentation. We aim to resolve claims within 5-7 business days.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Investigation Process</h3>
              <p className="mb-4">
                Our team will review your claim, contact the seller, and evaluate all evidence. Both parties have the opportunity to provide their perspective before a decision is made.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Resolution Outcomes</h3>
              <p>
                Possible outcomes include full refund, partial refund, replacement service, or claim denial if evidence doesn't support the claim. All decisions are final unless new evidence is presented.
              </p>
            </LegalSection>

            {/* Prohibited Items */}
            <LegalSection id="prohibited-items" title="Prohibited Items" icon={<Ban className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Illegal Products & Services</h3>
              <p className="mb-4">
                The following are strictly prohibited: illegal substances, weapons, stolen goods, counterfeit items, services that violate laws or regulations, and anything that promotes illegal activity.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Fraudulent or Deceptive Offerings</h3>
              <p className="mb-4">
                Prohibited: Pyramid schemes, get-rich-quick scams, misleading marketing materials, fake reviews or testimonials, falsified credentials, and products/services that cannot deliver promised results.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Harmful or Offensive Content</h3>
              <p className="mb-4">
                We do not allow: hate speech, discriminatory content, adult content, graphic violence, harassment tools, or services that facilitate harm to individuals or groups.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Intellectual Property Violations</h3>
              <p className="mb-4">
                Prohibited: Unauthorized use of copyrighted material, trademark infringement, stolen software or content, and services that facilitate piracy or IP theft.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Enforcement</h3>
              <p>
                Violations result in immediate listing removal, account suspension, and potential legal action. Repeat offenders are permanently banned. Users can report prohibited items through our reporting system.
              </p>
            </LegalSection>

            {/* Refund Policy */}
            <LegalSection id="refund-policy" title="Refund Policy" icon={<RefreshCw className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. General Refund Policy</h3>
              <p className="mb-4">
                We offer a 30-day money-back guarantee on most products and services. Refunds are issued to the original payment method within 5-10 business days after approval.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Eligible Refund Reasons</h3>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li>Service not delivered within promised timeframe</li>
                <li>Product significantly different from description</li>
                <li>Technical issues preventing access or use</li>
                <li>Seller violated terms of service</li>
                <li>Duplicate purchase or billing error</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Non-Refundable Items</h3>
              <p className="mb-4">
                The following are typically non-refundable: custom or personalized services, completed consultations, downloaded digital content after access, services used beyond trial period, and subscription fees (unless auto-renewed in error).
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Prorated Refunds</h3>
              <p className="mb-4">
                For subscription services cancelled mid-term, you may receive a prorated refund for the unused portion at the seller's discretion. One-time purchases are refunded in full if eligible.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Refund Process</h3>
              <p className="mb-4">
                To request a refund: (1) Contact the seller directly through our platform, (2) If unresolved, submit a refund request to our support team, (3) Provide order details and reason for refund, (4) Allow 5-7 business days for review.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Exceptions</h3>
              <p>
                Some sellers offer extended or custom refund policies. Always check the specific refund policy on the product page before purchasing.
              </p>
            </LegalSection>

            {/* Dispute Resolution */}
            <LegalSection id="dispute-resolution" title="Dispute Resolution" icon={<Scale className="w-7 h-7" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mt-6 mb-3">1. Informal Resolution</h3>
              <p className="mb-4">
                Before filing a formal dispute, we encourage users to attempt informal resolution by contacting the other party through our platform messaging system. Many issues can be resolved through direct communication.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Mediation Process</h3>
              <p className="mb-4">
                If informal resolution fails, either party may request mediation. Circle Pro Hub will assign a neutral mediator to facilitate communication and help parties reach a mutually acceptable solution.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Arbitration Clause</h3>
              <p className="mb-4">
                Any disputes not resolved through mediation shall be settled by binding arbitration under the rules of the American Arbitration Association. Arbitration will be conducted remotely unless both parties agree otherwise.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Class Action Waiver</h3>
              <p className="mb-4">
                You agree to resolve disputes on an individual basis only. You waive the right to participate in class actions, class arbitrations, or representative proceedings against Circle Pro Hub.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Escalation Path</h3>
              <p className="mb-4">
                Dispute resolution follows this path: (1) Direct communication between parties, (2) Platform support intervention, (3) Mediation, (4) Binding arbitration. Each step has specific timelines and procedures.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Governing Law</h3>
              <p className="mb-4">
                These terms and any disputes shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Contact for Disputes</h3>
              <p>
                For dispute resolution assistance, contact our legal team at disputes@circlenetwork.io or through our support portal. Include all relevant documentation and correspondence.
              </p>
            </LegalSection>

            {/* Contact Support */}
            <LegalSection id="contact-support" title="Contact Support" icon={<Mail className="w-6 h-6" />} lastUpdated="January 15, 2025">
              <h3 className="text-xl font-semibold mb-3">Get Help with Legal Questions</h3>
              <p className="mb-6">
                If you have any questions about these policies, need clarification on any terms, or require legal assistance, our support team is here to help. We respond to all inquiries within 1-2 business days.
              </p>

              <div className="bg-muted/50 rounded-lg p-6 mb-6 border">
                <h4 className="font-semibold mb-4 text-lg">Contact Information</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Email Support</p>
                      <a href="mailto:legal@circlenetwork.io" className="text-primary hover:underline">
                        legal@circlenetwork.io
                      </a>
                      <p className="text-sm text-muted-foreground mt-1">For general legal inquiries and policy questions</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Disputes & Refunds</p>
                      <a href="mailto:disputes@circlenetwork.io" className="text-primary hover:underline">
                        disputes@circlenetwork.io
                      </a>
                      <p className="text-sm text-muted-foreground mt-1">For transaction disputes and refund requests</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Privacy & Data Requests</p>
                      <a href="mailto:privacy@circlenetwork.io" className="text-primary hover:underline">
                        privacy@circlenetwork.io
                      </a>
                      <p className="text-sm text-muted-foreground mt-1">For GDPR, CCPA, and data deletion requests</p>
                    </div>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold mb-3">Business Hours</h4>
              <p className="mb-4">
                Our legal team is available Monday through Friday, 9:00 AM - 6:00 PM EST. While we strive to respond to all inquiries promptly, please allow up to 48 hours for a complete response to complex legal questions.
              </p>

              <h4 className="font-semibold mb-3">Mailing Address</h4>
              <p className="mb-4">
                  Circle Consulting LLC. Legal Department
501 Union St Ste 545 #651315
Nashville, Tennessee 37219-1876
United States<br />
                501 Union St Ste 545 #651315<br />
                Nashville, Tennessee 37219-1876<br />
                United States
              </p>

              <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                <p className="text-sm">
                  <strong>Note:</strong> For urgent security issues or data breaches, please contact our security team immediately at security@circlenetwork.io or call our 24/7 security hotline at 1-800-CIRCLE-911.
                </p>
              </div>
            </LegalSection>
          </div>
        </div>
      </main>

      {/* Scroll to Top Button */}
      <Button onClick={scrollToTop} size="lg" className={cn("fixed bottom-6 left-6 z-50 rounded-full shadow-lg h-14 w-14 p-0 transition-all", showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none")}>
        <ArrowUp className="w-6 h-6" />
      </Button>

      <Footer />
    </div>;
};
export default Terms;